// ConversationHistoryPanel.js
import React, { useState, useEffect, useRef } from 'react';
import '../styles/ConversationHistoryPanel.css';

function ConversationHistoryPanel({ 
  conversationHistory = [], 
  onLoadConversation, 
  onDeleteConversation, 
  onPinConversation,
  onRenameConversation,
  activeConversation, 
  onClose,
  theme
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const panelRef = useRef(null);
  const editInputRef = useRef(null);

  // Filter conversations based on search term
  useEffect(() => {
    // First sort conversations - pinned ones first
    const sorted = [...conversationHistory].sort((a, b) => {
      // Sort pinned conversations to the top
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      
      // Then sort by last updated date (newest first)
      return new Date(b.lastUpdated) - new Date(a.lastUpdated);
    });
    
    // Then filter based on search term
    const filtered = sorted.filter(conv => 
      conv.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredConversations(filtered);
  }, [searchTerm, conversationHistory]);

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  // Handle click outside to close panel
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Focus on the edit input when editing a conversation title
  useEffect(() => {
    if (editingConversationId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingConversationId]);

  const startRenaming = (conversation, e) => {
    e.stopPropagation();
    setEditingConversationId(conversation.sessionId);
    setNewTitle(conversation.title);
  };

  const handleRename = (e) => {
    e.preventDefault();
    if (newTitle.trim() && editingConversationId) {
      onRenameConversation(editingConversationId, newTitle.trim());
      setEditingConversationId(null);
      setNewTitle('');
    }
  };

  const handlePinToggle = (sessionId, isPinned, e) => {
    e.stopPropagation();
    onPinConversation(sessionId, !isPinned);
  };

  return (
    <div className="conversation-history-overlay">
      <div className="conversation-history-panel" ref={panelRef}>
        <div className="conversation-panel-header">
          <h2>Conversation History</h2>
          <button 
            className="panel-close-btn" 
            onClick={onClose}
          >
            <span className="material-icons">close</span>
          </button>
        </div>
        
        <div className="conversation-panel-search">
          <span className="material-icons search-icon">search</span>
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button 
              className="clear-search-btn" 
              onClick={() => setSearchTerm('')}
            >
              <span className="material-icons">clear</span>
            </button>
          )}
        </div>
        
        <div className="conversation-panel-list">
          {filteredConversations.length === 0 ? (
            <div className="no-conversations">
              {searchTerm ? "No matching conversations found" : "No conversations yet"}
            </div>
          ) : (
            filteredConversations.map(conversation => (
              <div 
                key={conversation.sessionId} 
                className={`conversation-panel-item ${activeConversation?.sessionId === conversation.sessionId ? 'active' : ''} ${conversation.isPinned ? 'pinned' : ''}`}
                onClick={() => onLoadConversation(conversation)}
              >
                <div className="conversation-panel-item-content">
                  {editingConversationId === conversation.sessionId ? (
                    <form onSubmit={handleRename} className="rename-form">
                      <input
                        ref={editInputRef}
                        type="text"
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        onBlur={() => setEditingConversationId(null)}
                        className="rename-input"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <button type="submit" className="rename-submit-btn" onClick={(e) => e.stopPropagation()}>
                        <span className="material-icons">check</span>
                      </button>
                    </form>
                  ) : (
                    <div className="conversation-panel-title">
                      {conversation.isPinned && <span className="material-icons pin-icon">push_pin</span>}
                      {conversation.title}
                    </div>
                  )}
                  <div className="conversation-panel-meta">
                    <span className="conversation-panel-date">
                      {formatDate(conversation.lastUpdated)}
                    </span>
                    <span className="conversation-panel-message-count">
                      {conversation.messages.length} messages
                    </span>
                  </div>
                </div>
                <div className="conversation-panel-actions">
                  <button
                    className="pin-conversation-btn"
                    onClick={(e) => handlePinToggle(conversation.sessionId, conversation.isPinned, e)}
                    title={conversation.isPinned ? "Unpin Conversation" : "Pin Conversation"}
                  >
                    <span className="material-icons">{conversation.isPinned ? 'star' : 'star_border'}</span>
                  </button>
                  <button 
                    className="rename-conversation-btn"
                    onClick={(e) => startRenaming(conversation, e)}
                    title="Rename Conversation"
                  >
                    <span className="material-icons">edit</span>
                  </button>
                  <button
                    className="delete-conversation-panel-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm("Are you sure you want to delete this conversation?")) {
                        onDeleteConversation(conversation.sessionId);
                      }
                    }}
                    title="Delete Conversation"
                  >
                    <span className="material-icons">delete</span>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="conversation-panel-footer">
          <div className="conversation-panel-stats">
            <h3>Powered by HCLSoftware</h3>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConversationHistoryPanel;